import { isLeafUnit, sameAssessmentUnit } from './assessmentUnitUtils';
import { chatApiSubCapabilityId } from './chatThreadUtils';

const STORAGE_PREFIX = "reimagine_chat_viewed";

export const CHAT_READ_STATE_CHANGED = "chat-read-state-changed";

export type ChatViewerRole = "client" | "sme";

export type ChatHistoryItem = {
  id?: number | string | null;
  senderName?: string | null;
  capabilityId?: number | string | null;
  subCapabilityId?: number | string | null;
  messageDate?: string | null;
  message?: string | null;
  attachments?: Array<{ attachmentUrl?: string | null }> | null;
};

export function getThreadMessagesNewestFirst(
  chatHistory: any[] | null | undefined,
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined
): any[] {
  if (!Array.isArray(chatHistory) || capabilityId == null) {
    return [];
  }
  if (!isLeafUnit(subCapabilityId) && subCapabilityId == null) {
    return [];
  }

  return chatHistory
    .filter((chat) =>
      sameAssessmentUnit(
        chat.capabilityId,
        chat.subCapabilityId,
        capabilityId,
        subCapabilityId
      )
    )
    .slice()
    .sort((a, b) => {
      const byId = (Number(b.id) || 0) - (Number(a.id) || 0);
      if (byId !== 0) {
        return byId;
      }
      const aTime = a.messageDate ? new Date(a.messageDate).getTime() : 0;
      const bTime = b.messageDate ? new Date(b.messageDate).getTime() : 0;
      return bTime - aTime;
    });
}

function getStorageKey(
  userId: number | string,
  projectId: number | string,
  capabilityId: number | string,
  subCapabilityId: number | string | null | undefined
): string {
  const thread = threadKey(capabilityId, chatApiSubCapabilityId(subCapabilityId));
  return `${STORAGE_PREFIX}:${userId}:${projectId}:${thread}`;
}

function notifyReadStateChanged(detail: {
  userId: number | string;
  projectId: number | string;
  capabilityId: number | string;
  subCapabilityId: number | string | null | undefined;
  messageDate: string;
}): void {
  try {
    window.dispatchEvent(new CustomEvent(CHAT_READ_STATE_CHANGED, { detail }));
  } catch {
    // Ignore environments without window/CustomEvent
  }
}

function threadKey(
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined
): string {
  return `${capabilityId ?? ""}:${chatApiSubCapabilityId(subCapabilityId)}`;
}

export function getViewedMessageDate(
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined
): string | null {
  if (userId == null || projectId == null || capabilityId == null) {
    return null;
  }
  if (!isLeafUnit(subCapabilityId) && subCapabilityId == null) {
    return null;
  }

  try {
    return localStorage.getItem(getStorageKey(userId, projectId, capabilityId, subCapabilityId));
  } catch {
    return null;
  }
}

export function markChatThreadViewed(
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined,
  messageDate: string | null | undefined
): void {
  if (userId == null || projectId == null || capabilityId == null || !messageDate) {
    return;
  }
  if (!isLeafUnit(subCapabilityId) && subCapabilityId == null) {
    return;
  }

  try {
    localStorage.setItem(
      getStorageKey(userId, projectId, capabilityId, subCapabilityId),
      messageDate
    );
    notifyReadStateChanged({
      userId,
      projectId,
      capabilityId,
      subCapabilityId,
      messageDate,
    });
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

export function isNotificationUnread(
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined,
  notificationMessageDate: string | null | undefined
): boolean {
  if (!notificationMessageDate) {
    return false;
  }

  const viewedDate = getViewedMessageDate(userId, projectId, capabilityId, subCapabilityId);
  if (!viewedDate) {
    return true;
  }

  return new Date(notificationMessageDate) > new Date(viewedDate);
}

/**
 * Messages that still need a response from the viewer, one per capability/sub-capability thread.
 * Sorted newest-first.
 */
export function getActionableMessages(
  chatHistory: ChatHistoryItem[] | null | undefined,
  viewerRole: ChatViewerRole
): ChatHistoryItem[] {
  if (!Array.isArray(chatHistory) || chatHistory.length === 0) {
    return [];
  }

  const isFromOtherParty =
    viewerRole === "client"
      ? (chat: ChatHistoryItem) => chat.senderName === "SME"
      : (chat: ChatHistoryItem) => Boolean(chat.senderName && chat.senderName !== "SME");

  const isFromViewer =
    viewerRole === "client"
      ? (chat: ChatHistoryItem) => Boolean(chat.senderName && chat.senderName !== "SME")
      : (chat: ChatHistoryItem) => chat.senderName === "SME";

  const latestByThread = new Map<string, ChatHistoryItem>();

  for (const chat of chatHistory) {
    if (!isFromOtherParty(chat) || chat.capabilityId == null || !chat.messageDate) {
      continue;
    }
    if (!isLeafUnit(chat.subCapabilityId) && chat.subCapabilityId == null) {
      continue;
    }

    const key = threadKey(chat.capabilityId, chat.subCapabilityId);
    const existing = latestByThread.get(key);
    if (
      !existing ||
      new Date(chat.messageDate).getTime() > new Date(existing.messageDate || 0).getTime()
    ) {
      latestByThread.set(key, chat);
    }
  }

  const actionable: ChatHistoryItem[] = [];

  for (const message of latestByThread.values()) {
    const viewerResponded = chatHistory.some(
      (chat) =>
        sameAssessmentUnit(
          chat.capabilityId,
          chat.subCapabilityId,
          message.capabilityId,
          message.subCapabilityId
        ) &&
        isFromViewer(chat) &&
        new Date(chat.messageDate || 0).getTime() >
          new Date(message.messageDate || 0).getTime()
    );

    if (!viewerResponded) {
      actionable.push(message);
    }
  }

  return actionable.sort(
    (a, b) =>
      new Date(b.messageDate || 0).getTime() - new Date(a.messageDate || 0).getTime()
  );
}

/** Latest message that still needs a response from the viewer (ignores local read state). */
export function getLatestActionableMessage(
  chatHistory: ChatHistoryItem[] | null | undefined,
  viewerRole: ChatViewerRole
): ChatHistoryItem | null {
  return getActionableMessages(chatHistory, viewerRole)[0] ?? null;
}

/** Actionable threads the viewer has not opened/viewed yet. Sorted newest-first. */
export function getUnreadActionableMessages(
  chatHistory: ChatHistoryItem[] | null | undefined,
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  viewerRole: ChatViewerRole
): ChatHistoryItem[] {
  return getActionableMessages(chatHistory, viewerRole).filter((message) =>
    isNotificationUnread(
      userId,
      projectId,
      message.capabilityId,
      message.subCapabilityId,
      message.messageDate
    )
  );
}

export function hasUnreadChatNotification(
  chatHistory: ChatHistoryItem[] | null | undefined,
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  viewerRole: ChatViewerRole
): boolean {
  return getUnreadActionableMessages(chatHistory, userId, projectId, viewerRole).length > 0;
}

export function hasUnreadForCapability(
  unreadThreads: ChatHistoryItem[] | null | undefined,
  capabilityId: number | string | null | undefined
): boolean {
  if (!Array.isArray(unreadThreads) || capabilityId == null) {
    return false;
  }

  return unreadThreads.some((thread) => `${thread.capabilityId}` === `${capabilityId}`);
}

export function hasUnreadForSubCapability(
  unreadThreads: ChatHistoryItem[] | null | undefined,
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined
): boolean {
  if (!Array.isArray(unreadThreads) || capabilityId == null) {
    return false;
  }
  if (!isLeafUnit(subCapabilityId) && subCapabilityId == null) {
    return false;
  }

  return unreadThreads.some((thread) =>
    sameAssessmentUnit(
      thread.capabilityId,
      thread.subCapabilityId,
      capabilityId,
      subCapabilityId
    )
  );
}
